<script setup>
import {
  defineEmits,
  defineProps,
  onMounted,
  nextTick,
  computed,
  watch,
  ref,
} from 'vue';

import {
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import { getExtendedMessageVariables } from 'shared/helpers/messageVariablesHelper';

import MarkdownIt from 'markdown-it';

import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useInbox } from 'dashboard/composables/useInbox';
import Icon from 'next/icon/Icon.vue';
import CannedResponseVariablesModal from 'dashboard/components/widgets/modal/CannedResponseVariablesModal.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  toEmail: {
    type: String,
    default: null,
  },
  tempCaseId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['onInsert', 'onClose', 'update:show']);

const searchInputRef = ref(null);
const searchKey = ref('');
const baseVariables = ref({});
const selectedCannedResponse = ref(null);
const missingVariables = ref([]);
const showVariablesModal = ref(false);
const toContact = ref({});

const store = useStore();

const cannedResponses = useMapGetter('getCannedResponses');
const { inbox: currentInbox } = useInbox(props.inboxId);

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const onClose = () => {
  emit('onClose');
};

const htmlConverter = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
});

const onInsert = markdownContent => {
  emit(
    'onInsert',
    htmlConverter.render(markdownContent),
    selectedCannedResponse.value?.id
  );
  onClose();
};

const loadContact = () => {
  if (!props.toEmail) {
    toContact.value = {};
    return;
  }

  store
    .dispatch('contacts/find', {
      email: props.toEmail,
    })
    .then(contact => {
      toContact.value = contact || {};
    });
};

const currentConversation = () => {
  return {
    case_id: props.tempCaseId,
    meta: {
      assignee: store.getters.getCurrentUser,
      sender: toContact.value,
    },
  };
};

const onSelect = cannedResponse => {
  baseVariables.value = getExtendedMessageVariables({
    conversation: currentConversation(),
    contact: toContact.value,
    inbox: currentInbox,
  });
  selectedCannedResponse.value = cannedResponse;
  missingVariables.value = getUndefinedVariablesInMessage({
    message: cannedResponse.content,
    variables: baseVariables.value,
  });
  showVariablesModal.value = !!missingVariables.value.length;
  if (!showVariablesModal.value) {
    const mdContent = replaceVariablesInMessage({
      message: cannedResponse.content,
      variables: baseVariables.value,
    });
    onInsert(htmlConverter.render(mdContent), cannedResponse.id);
  }
};

const fetchCannedResponses = () => {
  store.dispatch('getCannedResponse', {
    inboxId: props.inboxId,
  });
};

const filteredCannedResponses = computed(() => {
  if (!searchKey.value.trim()) {
    return cannedResponses.value;
  }
  return cannedResponses.value.filter(response =>
    response.short_code
      .toLowerCase()
      .includes(searchKey.value.trim().toLowerCase())
  );
});

const maxLength = computed(() => {
  return MESSAGE_MAX_LENGTH.GENERAL;
});

onMounted(() => {
  loadContact();
});

watch(
  () => props.show,
  newVal => {
    if (!newVal) return;

    nextTick(() => {
      if (searchInputRef.value) searchInputRef.value.focus();
    });
  }
);

watch(() => props.inboxId, fetchCannedResponses);
watch(() => props.toEmail, loadContact);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <woot-modal-header :header-title="$t('CANNED_MGMT.SEARCH_MODAL_TITLE')" />
    <div class="row modal-content">
      <input
        ref="searchInputRef"
        v-model="searchKey"
        :placeholder="$t('CANNED_MGMT.SEARCH_PLACEHOLDER')"
        type="text"
        class="w-full p-2 mb-4 border rounded"
      />
      <ul
        v-if="filteredCannedResponses.length > 0"
        class="list-none w-full flex flex-col overflow-y-auto max-h-[calc(100vh-12rem)] rounded-lg border border-n-weak dark:border-n-strong"
      >
        <li
          v-for="response in filteredCannedResponses"
          :key="response.id"
          class="cursor-pointer flex flex-col py-2 px-3 border-b border-n-weak hover:bg-n-alpha-2 dark:border-n-strong last:border-0"
          @click="onSelect(response)"
        >
          <p class="mb-0">{{ response.short_code }}</p>
          <span class="text-xs truncate w-full text-n-slate-10">
            {{ response.content }}
          </span>
        </li>
      </ul>
      <div
        v-else
        class="flex flex-col items-center justify-center text-n-slate-11 gap-1"
      >
        <Icon icon="i-lucide-heart-crack" class="text-4xl opacity-50" />
        <span>{{ $t('CANNED_MGMT.SEARCH_404') }}</span>
      </div>
    </div>
  </woot-modal>
  <CannedResponseVariablesModal
    :show="showVariablesModal"
    :max-length="maxLength"
    :variables="baseVariables"
    :missing-variables="missingVariables"
    :template="selectedCannedResponse?.content"
    @on-close="showVariablesModal = false"
    @on-submit="onInsert"
  />
</template>
